import { FC, useEffect, useRef, useState } from "react"
import { useTranslation } from "react-i18next"
import { useDispatch } from "react-redux"
import {
  Navigate,
  Route,
  Routes,
  useLocation,
  useParams,
} from "react-router-dom"
import ChevronLeftIcon from "@mui/icons-material/ChevronLeft"
import ChevronRightIcon from "@mui/icons-material/ChevronRight"
import { max } from "lodash"

import NavigationBlockPopup from "../../components/NavigationBlockPopup/NavigationBlockPopup"
import { updateCaseById } from "../../core/app/slices/case/caseThunkApi"
import { resetCase } from "../../core/app/slices/clinical/clinicalSlice"
import { setIsRouteChangeBlocked } from "../../core/app/slices/navigationPopup/navigationPopupSlice"
import { updateAlignerOrderPayload } from "../../core/app/slices/order"
import { placeAlignerOrders } from "../../core/app/slices/order/orderThunkApi"
import { RootState } from "../../core/app/store"
import { useCallbackPrompt } from "../../hooks/useCallbackPrompt"
import { useMyNavigation } from "../../hooks/useMyNavigation"
import { UButton,UText } from "../../ui-component/components"
import { AlertColor, Box, Container } from "../../ui-component/mui.components"

import Bundle from "./Bundle/Bundle"
import Shipping from "../../modules/Order/Shipping/Shipping"
import Stage from "./OrderStage"
import Customization,{CustomizationHandle} from "./Customization/Customization"
import ExitOrderAlertPopup from "../../modules/Order/ExitOrderAlertPopup"
import {
  getCoverage,
  getSelectedTeeth,
  getThickness,
  maxRangeVal,
  retainerStageRange,
  sortStage,
  stageRange,
} from "../../modules/Order/stage.helper"
import OrderSteps from "./Stepper"
import { caseManagement, orderView, stagingManager } from "@/gluelayer"
import { uploadCaseZips } from "@/core/app/slices/clinical/clinicalThunkApi"
import { fetchDoctorsList } from "@/core/app/slices/doctors/doctorThunkApi"
import {
  fetchUserPreferences,
  getCreditCardDetails,
  getSystemMessage,
  updateUserPreferences,
} from "../../core/app/slices/user/userApis"
import { useAppDispatch, useAppSelector } from "@/core/app/hooks"
import { fetchFormLists } from "@/core/app/slices/uassist/formThunkApi"
import ReviewOrder from "./ReviewOrder/ReviewOrder"
import OrderConfirmation from './Confirmation/Confirmation'
import { IAlignerOrderPayload } from "@/core/model/interface/AlignerOrder"
import { getAWSRUMClient } from "@/core/global/awsRumClient"
/**
 * record placeorder event
 * @param payload 
 */
const recordPlaceorderEvent = (payload)=>{
  getAWSRUMClient()?.recordEvent('clinical_placeorder_event',{
    ...payload
  })
}


// 步骤配置类型
type StepConfig = {
  id: string;
  component: React.ReactElement;
  label?: string;
}

const Order: FC = () => {
  const { patientId, caseId } = useParams()
  const [isNextClicked, setIsNextClicked] = useState<boolean>(false)
  const navigate = useMyNavigation()
  const dispatch = useAppDispatch()
  const { t } = useTranslation("common")
  const [showAlert, setShowAlert] = useState<boolean>(false)

  const [stageAlertMessage, setstageAlertMessage] = useState<AlertColor>("info")
  const [customizationStep, setCustomizationStep] = useState<string>("1")
  const [cuStepTmp, setCuStep] = useState<number[]>([1])

  const customizationCompRef = useRef<CustomizationHandle>(null)
  // const steps = [
  //   {
  //     id: "bundle",
  //     label: "Select Bundle",
  //     to: `/order/patient/${patientId}/case/${caseId}/bundle`,
  //   },
  //   {
  //     id: "stage",
  //     label: "Select Stages",
  //     to: `/order/patient/${patientId}/case/${caseId}/stage`,
  //   },
  //   {
  //     id: "customization",
  //     label: "Customization",
  //     to: `/order/patient/${patientId}/case/${caseId}/customization`,
  //   },
  //   {
  //     id: "shipping",
  //     label: "Shipping & Packaging",
  //     to: `/order/patient/${patientId}/case/${caseId}/shipping`,
  //   },
  // ]

    // 步骤配置重构为组件映射
    const steps: StepConfig[] = [
      { 
        id: "bundle",
        component: <Bundle isSubmitted={isNextClicked} />,
        label: t("order.stepLabels.bundle"),
      },
      {
        id: "stage",
        component: <Stage
                      setStageAlertMessage={setstageAlertMessage}
                      alertMessage={stageAlertMessage} 
                    />,
        label: t("order.stepLabels.stage"),
      },{
        id:'customization',
        component: <Customization ref={customizationCompRef} />,
        label: t("order.stepLabels.customization"),
      },
      {
        id:'shipping',
        component: <Shipping />,
        label: t("order.stepLabels.shipping"),
      }
      // ,{
      //   id:'review',
      //   component: <ReviewOrder onPlaceOrder={()=>{
      //     setHasPlaceOrder(true)
      //   }}/>,
      //   label: t("overview.order.review"),
      // }
    ]

    /**
     * instead of fetching all the data in header.tsx
     */
  useEffect(()=>{
    dispatch(fetchDoctorsList())
    dispatch(fetchUserPreferences())
    dispatch(getCreditCardDetails())
    dispatch(getSystemMessage())
    dispatch(fetchFormLists())
  },[])

  useEffect(() => {
      let canvas = document.getElementById('canvas')
      if( canvas )
      {
        canvas.tabIndex = 0
        canvas.focus()
      }
      console.log('canvas focus: ', canvas)    
  }, [])

  const { caseDetail } = useAppSelector((state: RootState) => state.caseService)

  const {
    selectedBundle,
    shippingMethod,
    packageType,
    isAlignerCase,
    selectedShippingToAddressId,
    alignerOrderPayload,
    alignerOrderPayload: { stage, coverage, extra, recession, scanned },
    stageDetail,
    orderList,
  } = useAppSelector((state: RootState) => state.orderService)
  const [activeStep, setActiveStep] = useState(0)
  const [hasPlaceOrder, setHasPlaceOrder] = useState<number|undefined>(undefined)
  const [hasReviewOrder, setHasReviewOrder] = useState(false)
  // const [completed, setCompleted] = useState<{
  //   [k: number]: boolean
  // }>({ 0: false, 1: false, 2: false, 3: false })

  // const location = useLocation()

  // const { isRouteChangeBlocked } = useAppSelector(
  //   (state: RootState) => state.navigationPopup,
  // )

  // const [showPrompt, confirmNavigation, cancelNavigation] =
  //   useCallbackPrompt(isRouteChangeBlocked)

  // useEffect(() => {
  //   if (selectedBundle) {
  //     dispatch(setIsRouteChangeBlocked({ isRouteChangeBlocked: true }))
  //   }
  //   if (location?.pathname.includes("/bundle")) {
  //     setActiveStep(0)
  //   } else if (location?.pathname.includes("/stage")) {
  //     setActiveStep(1)
  //   } else if (location?.pathname.includes("/customization")) {
  //     setActiveStep(2)
  //   } else if (location?.pathname.includes("/shipping")) {
  //     createAlignerOrderParams()
  //     setActiveStep(3)
  //   } else {
  //     setActiveStep(0)
  //   }
  // }, [location, selectedBundle])

  // const totalSteps = () => {
  //   return steps.length
  // }

  // const completedSteps = () => {
  //   return Object.keys(completed).length
  // }

  // const isLastStep = () => {
  //   return activeStep === totalSteps() - 1
  // }

  // const allStepsCompleted = () => {
  //   return completedSteps() === totalSteps()
  // }

  const createAlignerOrderParams = () => {
    let payload = {} as any
    if (selectedBundle) {
      payload = {
        order_type: "Aligner Order",
        case_id: +caseId,
        pricing_option: selectedBundle.name,
        shipping_method: shippingMethod,
        package_type: packageType,
        pricing_id: selectedBundle.id,
        shipping_address_id: selectedShippingToAddressId,
        quote: {
          ...selectedBundle,
        },
        aligner_case: isAlignerCase,
        upper_stage_range: "",
        lower_stage_range: "",
        upper_stage_count: 0,
        lower_stage_count: 0,
        upper_retainer_stage: "",
        lower_retainer_stage: "",
        upper_template_stage: "",
        lower_template_stage: "",
        aligner_material: "",
        thickness: "",
        upper_left_aligner_coverage: "Full",
        upper_right_aligner_coverage: "Full",
        lower_left_aligner_coverage: "Full",
        lower_right_aligner_coverage: "Full",
        upper_left_lasttoothtocover: "",
        upper_right_lasttoothtocover: "",
        lower_left_lasttoothtocover: "",
        lower_right_lasttoothtocover: "",
        extra_info: '{"keep_ramp":false}',
        complete_upper_stage: "",
        complete_lower_stage: "",
        complete_upper_retainer: "",
        complete_lower_retainer: "",
        complete_upper_template: "",
        complete_lower_template: "",
        txplan_name: "",
        txplan_subname: "",
      }

      if (stage) {
        if (caseDetail && caseDetail.udesign_json) {
          const udesign_json = JSON.parse(caseDetail.udesign_json)
          //Upper Aligner
          /**
           * when the case is Aligner
           * user selected stage and  retainer
           */
          if (
            stage.aligners.up &&
            stage.aligners.up.length > 0 &&
            udesign_json.UpperType === "A"
          ) {
            payload.upper_stage_range = sortStage(stage.aligners.up).join(",")
            payload.upper_stage_count = stage.aligners.up.length
            if (stage.retainers.up > 0) {
              payload.upper_retainer_stage = retainerStageRange(
                stage.retainers.up,
                500 +
                  Math.max(stagingManager.wasmStageData.keypointsUpMaxStep, 0) +
                  stagingManager.wasmStageData.upStartStep,
              )
            }
          }
          /**
           * when the case is Aligner
           * user selected only retainer and not selected ny stages
           */
          if (
            stage.aligners.up.length === 0 &&
            stage.retainers.up > 0 &&
            udesign_json.UpperType === "A"
          ) {
            payload.upper_retainer_stage = retainerStageRange(
              stage.retainers.up,
              500 +
                Math.max(stagingManager.wasmStageData.keypointsUpMaxStep, 0) +
                stagingManager.wasmStageData.upStartStep,
            )
          }

          //Upper Retainer
          /**
           * when the case is Retainer
           * user selected  retainer
           */
          if (udesign_json.UpperType === "R") {
            if (stage.retainers.up > 0) {
              payload.upper_retainer_stage = retainerStageRange(
                stage.retainers.up,
                500,
              )
            }
          }

          //upper Template
          /**
             0：400
             1：400
             2：401
             3：402 
           */
          if (stage.templates.up || stage.templates.up == 0) {
            if (stage.templates.up > 1) {
              payload.upper_template_stage = stage.templates.up + 400 + ""
            } else {
              payload.upper_template_stage = "400"
            }
          }

          //Lower Aligner
          /**
           * when the case is Aligner
           * user selected stage and  retainer
           */
          if (
            stage.aligners.low &&
            stage.aligners.low.length > 0 &&
            udesign_json.LowerType === "A"
          ) {
            payload.lower_stage_range = sortStage(stage.aligners.low).join(",")
            payload.lower_stage_count = stage.aligners.low.length

            if (stage.retainers.low > 0) {
              payload.lower_retainer_stage = retainerStageRange(
                stage.retainers.low,
                500 +
                  Math.max(
                    stagingManager.wasmStageData.keypointsDownMaxStep,
                    0,
                  ) +
                  stagingManager.wasmStageData.lowStartStep,
              )
            }
          }
          /**
           * when the case is Aligner
           * user selected only retainer and not selected ny stages
           */
          if (
            stage.aligners.low.length === 0 &&
            stage.retainers.low > 0 &&
            udesign_json.LowerType === "A"
          ) {
            payload.lower_retainer_stage = retainerStageRange(
              stage.retainers.low,
              500 +
                Math.max(stagingManager.wasmStageData.keypointsDownMaxStep, 0) +
                stagingManager.wasmStageData.lowStartStep,
            )
          }

          //Lower Retainer
          if (udesign_json.LowerType === "R") {
            if (stage.retainers.low > 0) {
              payload.lower_retainer_stage = retainerStageRange(
                stage.retainers.low,
                500,
              )
            }
          }

          //Lower Template
          /**
             0：400
             1：400
             2：401
             3：402 
           */
          if (stage.templates.low || stage.templates.low == 0) {
            if (stage.templates.low > 1) {
              payload.lower_template_stage = stage.templates.low + 400 + ""
            } else {
              payload.lower_template_stage = "400"
            }
          }
        }
      }

      const coverageInfo:any = coverage
      if (coverageInfo) {
        //radioLeft：[upper_left_aligner_coverage,lower_left_aligner_coverage]
        if (coverageInfo.radioLeft) {
          payload.upper_left_aligner_coverage = getCoverage(
            coverageInfo.radioLeft[0] + "",
          )
          payload.lower_left_aligner_coverage = getCoverage(
            coverageInfo.radioLeft[1] + "",
          )
        }
        // radioRight: [upper_right_aligner_coverage,lower_right_aligner_coverage]

        if (coverageInfo.radioRight) {
          payload.upper_right_aligner_coverage = getCoverage(
            coverageInfo.radioRight[0] + "",
          )
          payload.lower_right_aligner_coverage = getCoverage(
            coverageInfo.radioRight[1] + "",
          )
        }

        //selectedTeethid[upper_right_lasttoothtocover,upper_left_lasttoothtocover, lower_left_lasttoothtocover, lower_right_lasttoothtocover]
        if (coverageInfo.selectedTeethid) {
          payload.upper_right_lasttoothtocover = getSelectedTeeth(
            coverageInfo.selectedTeethid[0],
          )
          payload.upper_left_lasttoothtocover = getSelectedTeeth(
            coverageInfo.selectedTeethid[1],
          )
          payload.lower_right_lasttoothtocover = getSelectedTeeth(
            coverageInfo.selectedTeethid[3],
          )
          payload.lower_left_lasttoothtocover = getSelectedTeeth(
            coverageInfo.selectedTeethid[2],
          )
        }
        if (coverageInfo.material) {
          payload.aligner_material =
            coverageInfo.material === "reva" ? "reva" : "zendura"
          payload.thickness =
            coverageInfo.material !== "reva"
              ? getThickness(coverageInfo.material + "")
              : "027"
        }
      }

      if (coverageInfo.keepRamp) {
        payload.extra_info = '{"keep_ramp":true}'
      }

      if (caseDetail && caseDetail.udesign_json) {
        const udesign_json = JSON.parse(caseDetail.udesign_json)
        if (udesign_json.UpperMaxStageNum && udesign_json.UpperType === "A") {
          payload.complete_upper_stage = stageRange(
            stagingManager.wasmStageData.keypointsUpMaxStep,
            stagingManager.wasmStageData.upStartStep + 1,
          )
          // payload.complete_upper_retainer =
          //   500 + maxRangeVal(+udesign_json.UpperMaxStageNum) + ""
        }
        if (udesign_json.LowerMaxStageNum && udesign_json.LowerType === "A") {
          payload.complete_lower_stage = stageRange(
            stagingManager.wasmStageData.keypointsDownMaxStep,
            stagingManager.wasmStageData.lowStartStep + 1,
          )
          // payload.complete_lower_retainer =
          //   500 + maxRangeVal(+udesign_json.LowerMaxStageNum) + ""
        }

        //TBD:after verifiying UD code  reainer values are 500 default
        if (udesign_json.UpperType === "R" || udesign_json.UpperType === "A") {
          payload.complete_upper_retainer = "500"
        }
        //TBD:after verifiying UD code  reainer values are 500 default
        if (udesign_json.LowerType === "R" || udesign_json.LowerType === "A") {
          payload.complete_lower_retainer = "500"
        }

        if (udesign_json.UpperTemplate > 0) {
          payload.complete_upper_template = "400"
        }
        if (udesign_json.LowerTemplate > 0) {
          payload.complete_lower_template = "400"
        }
      }

      if (selectedBundle.name === "A La Carte") {
        payload.pricing_option = "Alacarte"
        payload.pricing_id = ""
      }
      if (selectedBundle.name === "2 Retainers") {
        payload.pricing_option = "2 Retainer"
      }
      if (selectedBundle.name === "4 Retainers") {
        payload.pricing_option = "4 Retainer"
      }

      if (caseDetail.txplanName || caseDetail.txplanSubname) {
        payload.txplan_name = caseDetail.txplanName || ""
        payload.txplan_subname = caseDetail.txplanSubname || ""
      }

      dispatch(updateAlignerOrderPayload(payload))

      //Testing Purpose  we will remove it later
      console.log(
        "...udesign_json...",
        caseDetail.udesign_json,
        "... extra...",
        extra,
        "...recessionfill",
        recession,
        "...scanned...",
        scanned,
        "...coverage...",
        alignerOrderPayload.coverage,
        ".dddddd.final .payload",
        payload,
      )
    }
    return payload
  }

  const getStageValues = () => {
    let upperPrintArr = []
    let lowerPrintArr = []
    let upperRetainer = 0
    let lowerRetainer = 0
    let upperTemplate = 0
    let lowerTemplate = 0
    if (orderList && orderList[caseDetail?.id]?.length > 0) {
      orderList[caseDetail.id].forEach((res) => {
        if (res?.upperStageRange) {
          upperPrintArr.push(...res.upperStageRange.split(","))
        }
        if (res?.lowerStageRange) {
          lowerPrintArr.push(...res.lowerStageRange.split(","))
        }
        if (res?.upperRetainerCount) upperRetainer = 1

        if (res?.lowerRetainerCount) lowerRetainer = 1

        if (res?.upperTemplateStage) upperTemplate = 1

        if (res?.lowerTemplateStage) lowerTemplate = 1
      })
    }

    upperPrintArr = [...upperPrintArr, ...alignerOrderPayload.stage.aligners.up]
    lowerPrintArr = [
      ...lowerPrintArr,
      ...alignerOrderPayload.stage.aligners.low,
    ]
    if (stage.retainers.up) upperRetainer = 1
    if (stage.retainers.low) lowerRetainer = 1
    if (stage.templates.up || stage.templates.up == 0) upperTemplate = 1
    if (stage.templates.low || stage.templates.low == 0) lowerTemplate = 1

    return {
      upper: upperPrintArr.length ? Math.max(...upperPrintArr.map(Number)) : 0,
      lower: lowerPrintArr.length ? Math.max(...lowerPrintArr.map(Number)) : 0,
      uRetainer: upperRetainer,
      lRetainer: lowerRetainer,
      uTemplate: upperTemplate,
      lTemplate: lowerTemplate,
    }
  }

  const updateJsonToBulk2Zip = () => {
    const elasticvalue = orderView.getElasticJson(scanned)
    caseManagement
      .saveJsonDataToZip(
        elasticvalue,
        "elasticControlPoints.json",
        "Setting Data2",
      )
      .then((bool) => {
        if (bool) {
          caseManagement.getCaseFiles(["bulk2"]).then((r) => {
            const zips = []
            if (r) {
              for (const k in r) {
                zips.push({
                  fileName: k,
                  file: r[k],
                })
              }
              dispatch(
                uploadCaseZips({
                  zips,
                  patientId,
                  caseId,
                }),
              )
            }
          })

          console.log(
            "elasticControlPoints.json has been successfully deposited to the virtual directory!",
          )
        } else {
          console.log(
            "Failed to store elasticControlPoints.json to virtual directory!",
          )
        }
      })
  }

  const submitAlignerOrder = () => {

    const param = createAlignerOrderParams() as IAlignerOrderPayload
    const { upper, lower, uRetainer, uTemplate, lRetainer, lTemplate } =
      getStageValues()
    dispatch(placeAlignerOrders(param)).then((_res) => {
      //update recessionfill
      if (caseDetail && caseDetail.udesign_json) {
        const udesign_json = JSON.parse(caseDetail.udesign_json)
        const recessionfill = []
        // eslint-disable-next-line prettier/prettier
        recession && recession.forEach((x) => recessionfill.push({ id: x }))
        const newUdesignJsonObj = {
          ...udesign_json,
          recessionfill:
            recession && recession.length ? JSON.stringify(recessionfill) : "",
          UpperPrintStageNum: upper,
          LowerPrintStageNum: lower,
          LowerRetainer: lRetainer,
          UpperRetainer: uRetainer,
          UpperTemplate: uTemplate,
          LowerTemplate: lTemplate,
        }
        if (scanned) {
          updateJsonToBulk2Zip()
        }
        dispatch(
          updateCaseById({
            patientId,
            caseId,
            payload: {
              case_extra_attrs: JSON.stringify(newUdesignJsonObj),
            },
          }),
        )
        recordPlaceorderEvent({
          params:param,
          case_extra_attrs:newUdesignJsonObj
        })
      }
      // navigate(`/order/patient/${patientId}/case/${caseId}/revieworder`)
    })
  }


  const handleNext = async (prev: number)=>{
    console.log('handleNext',prev)
    if(prev===0){
      // select bundle
      if (
        !selectedBundle &&
        (!selectedBundle || (selectedBundle && !selectedBundle.sku_name))
      ) {
        // to scroll top to see warning
        window.scrollTo({
          top: 50,
          behavior: "smooth",
        })
        console.warn('should select a bundle')
        return
      }
    }else if(prev===1){
      
      // select stage
      const isStage = validateStageSelection()
      console.log('select stage',isStage)
      if (!isStage) {
        setstageAlertMessage("error")
        return
      } else {
        setstageAlertMessage("info")
      }
    }else if(prev===2){
      // customization
      customizationCompRef.current?.uploadCoverageData()
      // history action, I don't known why, but the code code need do this before mount the shipping page
      createAlignerOrderParams()
    }
    // else if(prev===3){
    //   console.log('?????????????????')
    //   // shipping
    //   submitAlignerOrder()
    // }
    setActiveStep(prev=> Math.min(prev+1, steps.length-1))
  }

  const handleBack = async () => {
    // await dispatch(setIsRouteChangeBlocked({ isRouteChangeBlocked: false }))
    // if (activeStep === 0) {
    //   setShowAlert(true)
    // } else if (activeStep === 2) {
    //   if (customizationStep !== "1") {
    //     const step = +customizationStep
    //     setCustomizationStep("" + (step - 1))
    //     return
    //   } else {
    //     setCustomizationStep("1")
    //     setCuStep([1])
    //     setActiveStep((prevActiveStep) => prevActiveStep - 1)
    //     navigate(steps[activeStep - 1].to)
    //   }
    // } else {
    //   setActiveStep((prevActiveStep) => prevActiveStep - 1)
    //   navigate(steps[activeStep - 1].to)
    // }
    setActiveStep((prevActiveStep) => prevActiveStep - 1)
    // navigate(steps[activeStep - 1].to)
  }

  // const handleStep = (step: number) => () => {
  //   setActiveStep(step)
  // }

  // const handleComplete = () => {
  //   const newCompleted = completed
  //   newCompleted[activeStep] = true
  //   setCompleted(newCompleted)
  //   // handleNext()
  // }

  useEffect(() => {
    if (!stage || !selectedBundle) return
    const selectedAligners =
        stage.aligners.up.length + stage.aligners.low.length || 0,
      selectedRetainers = stage.retainers.up + stage.retainers.low || 0
    if (selectedAligners === 0 && selectedRetainers === 0) {
      setstageAlertMessage("info")
    }

    let aligners = 0
    let retainers = 0
    //when selected bundle
    if (selectedBundle.description) {
      if (selectedBundle.description.retainer) {
        retainers = selectedBundle.description.retainer.included
      }
      if (selectedBundle.description.aligner) {
        aligners = selectedBundle.description.aligner.included
      }
    }

    //when bundle as alacarte
    if (!selectedBundle.description && selectedBundle.retainer) {
      retainers = selectedBundle.retainer.item_count
    }
    //when bundle as alacarte
    if (!selectedBundle.description && selectedBundle.aligner) {
      aligners = selectedBundle.aligner.item_count
    }
    const bundle = { aligners, retainers }
    let consumed_aligner = 0,
      consumed_retainer = 0,
      consumed_template = 0
    for (let index = 0; index < stageDetail.length; index++) {
      const { stage } = stageDetail[index]
      if (!stage) continue
      if (stage < 400) ++consumed_aligner
      else if (stage < 500) ++consumed_template
      else ++consumed_retainer
    }
    const total_aligner = consumed_aligner + selectedAligners
    const total_retainer = consumed_retainer + selectedRetainers
    const isBundleAlignerCompleted = consumed_aligner >= bundle.aligners
    const isBundleretainerCompleted = consumed_retainer >= bundle.retainers
    if (
      ((isBundleAlignerCompleted && selectedAligners > 0) ||
        (!isBundleAlignerCompleted && selectedAligners > bundle.aligners) ||
        (!isBundleretainerCompleted && selectedRetainers > bundle.retainers)) &&
      selectedBundle.name !== "A La Carte"
    ) {
      setstageAlertMessage("warning")
    } else if (
      total_aligner <= bundle.aligners ||
      total_retainer <= bundle.retainers
    ) {
      setstageAlertMessage("info")
    }
  }, [stage, selectedBundle])

  const validateStageSelection = () => {
    const selectedAligners =
        stage.aligners.up.length + stage.aligners.low.length || 0,
      selectedRetainers = stage.retainers.up + stage.retainers.low || 0
    if (
      selectedAligners === 0 &&
      selectedRetainers === 0 &&
      stage.templates.up === null &&
      stage.templates.low === null
    ) {
      return false
    } else {
      return true
    }
  }
  //Clean up clincal zipNames, zipList, and other data to avoid entering clincal with errors
  useEffect(() => {
    return () => {
      dispatch(resetCase())
    }
  }, [])

  const getReviewOrder = () => {
    return  <>
    <Box
      sx={{
        display: "flex",
        justifyContent: "center",
        alignItems: "center",
      }}
    >
      <UText component={"div"} variant="h4">
        {t("overview.order.review")}
      </UText>
    </Box>
    <ReviewOrder 
      onPlaceOrder={(orderId)=>{
        setHasPlaceOrder(orderId)
      }}/>
      <Container sx={{ mb: 3 }}>
        <Box
          sx={{
            display: "flex",
            flexDirection: "row",
          }}
        >
          <UButton
            variant="outlined"
            onClickHandler={()=>{setHasReviewOrder(false)}}
            sxProp={{ width: "96px", height: "36px" }}
            startIcon={
              <ChevronLeftIcon fontSize={"small"} sx={{ marginRight: "3px" }} />
            }
            btnText={t("button.back")}
          />
        </Box>
      </Container>
    </>
  }

  return (
    <>
      {
        hasPlaceOrder ?<OrderConfirmation orderId={hasPlaceOrder}/>:hasReviewOrder?
        getReviewOrder() : 
        <>
          <OrderSteps activeStep={activeStep} steps={steps as any} />
          <Box
            sx={{
              minHeight: "300px",
            }}
          >
            {steps[activeStep].component}
          </Box>
          <Container sx={{ mb: 3 }}>
            <Box
              sx={{
                display: "flex",
                flexDirection: "row",
              }}
            >
              <UButton
                variant="outlined"
                onClickHandler={handleBack}
                sxProp={{ width: "96px", height: "36px" }}
                startIcon={
                  <ChevronLeftIcon fontSize={"small"} sx={{ marginRight: "3px" }} />
                }
                btnText={t("button.back")}
              />
              <Box sx={{ flex: "1 1 auto" }} />
              {activeStep === steps.length-1 ? (
                // if on the last step, don't show anything here
                <UButton
                  variant="contained"
                  onClickHandler={()=>{
                    setHasReviewOrder(true)
                    // place order
                    submitAlignerOrder()
                  }}
                  sxProp={{ width: "109px", height: "36px" }}
                  endIcon={
                    <ChevronRightIcon
                      fontSize={"small"}
                      sx={{ marginRight: "3px" }}
                    />
                  }
                  btnText={t("button.review")}
                />
              ) : (
                <UButton
                  variant="contained"
                  onClickHandler={()=>handleNext(activeStep)}
                  sxProp={{ width: "92px", height: "36px" }}
                  endIcon={
                    <ChevronRightIcon
                      fontSize={"small"}
                      sx={{ marginRight: "3px" }}
                    />
                  }
                  btnText={t("button.next")}
                />
              )}
            </Box>
          </Container>
        </>
      }
    </>
  )
}

export default Order
